import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import * as lodash from 'lodash';
import type { IBlueprint, IFullLocation } from '~/domain';
import { ApiRequestError, ApiServerError, unwrapFetch2 } from '~/fetch';
import { LocalStorage } from '~/helpers';
import type { IToastMessage } from '~/helpers/Toastr';
import type { IAppState } from '~/appstate';
import * as Hub from '../ogm-hub';
import * as BlueprintsSlice from './BlueprintsSlice';
import * as SuppliersSlice from './SuppliersSlice';

const countPerPage = 24;

export interface IBlueprintManagementPageState {
	blueprintIds: number[];
	blueprintInProcess: boolean;
	blueprintError: boolean;
	locations: IFullLocation[];
	totalPages: number;
	totalResults: number;
	deleteModal: {
		isOpen: boolean;
		blueprintToDelete: IBlueprint;
		inProcess: boolean;
	};
	customerId: number;
	customerNote: string;
	currentPage: number;
	supplierFilterId: number;
	locationFilterIds: number[];
	searchTerm: string;
}

const initialPageState: IBlueprintManagementPageState = {
	blueprintIds: [],
	blueprintInProcess: false,
	blueprintError: false,
	locations: [],
	totalPages: 0,
	totalResults: 0,
	deleteModal: {
		isOpen: false,
		blueprintToDelete: undefined as unknown as IBlueprint,
		inProcess: false,
	},
	customerId: 0,
	customerNote: '',
	currentPage: 1,
	supplierFilterId: 0,
	locationFilterIds: [],
	searchTerm: '',
};

export interface IGetBlueprintsParameters {
	pageNumber?: number;
	supplierFilterId?: number;
	locationFilterIds?: number[];
	searchTerm?: string;
}

export interface IGetLocationParameters {
	supplierFilterId?: number;
}

export const getBlueprints = createAsyncThunk<
	Hub.ResultSet<Hub.BlueprintListing>,
	IGetBlueprintsParameters,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'blueprintManagementPage/getBlueprints',
	async (
		params: IGetBlueprintsParameters,
		{ rejectWithValue, getState, dispatch }
	) => {
		let { pageNumber, supplierFilterId, locationFilterIds, searchTerm } =
			params;

		if (
			pageNumber !== undefined &&
			!Number.isNaN(pageNumber) &&
			pageNumber > 0
		) {
			dispatch(
				blueprintManagementPageSlice.actions.updatePageNumber(pageNumber)
			);
		} else {
			pageNumber = getState().blueprintManagementPage.currentPage;
		}

		if (
			supplierFilterId !== undefined &&
			!Number.isNaN(supplierFilterId) &&
			supplierFilterId >= 0
		) {
			dispatch(
				blueprintManagementPageSlice.actions.updateSupplierFilterId(
					supplierFilterId
				)
			);
		} else {
			supplierFilterId = getState().blueprintManagementPage.supplierFilterId;
		}

		if (locationFilterIds !== undefined) {
			dispatch(
				blueprintManagementPageSlice.actions.updateLocationFilterIds(
					locationFilterIds
				)
			);
		} else {
			locationFilterIds = getState().blueprintManagementPage.locationFilterIds;
		}

		if (searchTerm !== undefined) {
			dispatch(
				blueprintManagementPageSlice.actions.updateSearchTerm(searchTerm)
			);
		} else {
			searchTerm = getState().blueprintManagementPage.searchTerm;
		}

		dispatch(blueprintManagementPageSlice.actions.clearBlueprints());
		const result = await dispatch(
			BlueprintsSlice.fetchBlueprints({
				countPerPage,
				pageNumber,
				supplierFilterId,
				locationFilterIds,
				searchTerm,
			})
		);

		if (BlueprintsSlice.fetchBlueprints.fulfilled.match(result)) {
			return result.payload;
		} else {
			rejectWithValue(null);
		}
	}
);

export interface IDeleteBlueprintParameters {
	blueprintId: number;
	currentPage: number;
	supplierFilterId?: number;
	searchTerm?: string;
	successMessage: IToastMessage;
	failureMessage: IToastMessage;
}

export const deleteBlueprint = createAsyncThunk<
	void,
	IDeleteBlueprintParameters,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'blueprintManagementPage/deleteBlueprint',
	async (
		params: IDeleteBlueprintParameters,
		{ getState, dispatch, rejectWithValue }
	) => {
		const numBlueprintsOnPageAfterDelete =
			getState().blueprintManagementPage.blueprintIds.length - 1;

		const pageNum =
			numBlueprintsOnPageAfterDelete === 0
				? params.currentPage - 1
				: params.currentPage;

		dispatch(
			blueprintManagementPageSlice.actions.removeBlueprint(params.blueprintId)
		);

		const result = await dispatch(
			BlueprintsSlice.deleteBlueprint({
				blueprintId: params.blueprintId,
				successMessage: params.successMessage,
				failureMessage: params.failureMessage,
			})
		);

		if (!BlueprintsSlice.deleteBlueprint.fulfilled.match(result)) {
			dispatch(
				blueprintManagementPageSlice.actions.addBlueprint(params.blueprintId)
			);
			rejectWithValue(null);
		} else {
			dispatch(
				getBlueprints({
					pageNumber: pageNum,
					supplierFilterId: params.supplierFilterId,
					searchTerm: params.searchTerm,
				})
			);
		}
	}
);

export const getSuppliers = createAsyncThunk<
	void,
	void,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'blueprintManagementPage/getSuppliers',
	async (_: void, { rejectWithValue, dispatch }) => {
		try {
			await dispatch(SuppliersSlice.fetchCurrentCustomerSuppliers());
			await dispatch(fetchLocations({}));
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export const getCustomerNote = createAsyncThunk<
	string,
	void,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'blueprintManagementPage/getCustomerNote',
	async (_: void, { rejectWithValue, getState }) => {
		try {
			const result = await unwrapFetch2(
				Hub.CustomerController.GetCustomerNoteForCustomer(
					getState().blueprintManagementPage.customerId
				)
			);
			return result.customerNote;
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export const fetchLocations = createAsyncThunk<
	Hub.FullLocation[],
	IGetLocationParameters,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'blueprintManagementPage/fetchLocations',
	async (params: IGetLocationParameters, { rejectWithValue, getState }) => {
		try {
			const customerId = LocalStorage.getCustomerId();
			const supplierFilterIsSet =
				params.supplierFilterId !== undefined && params.supplierFilterId != 0;
			const state = getState();
			const locationsResponse = await unwrapFetch2(
				Hub.LocationDistributionCenterController.GetLocationsForCustomerAndMultipleSuppliers(
					{
						customerId: customerId,
						supplierIds: supplierFilterIsSet
							? new Array(params.supplierFilterId)
							: Object.values(state.suppliers.customerSuppliersByCode).map(
									(x) => x.id
							  ),
					}
				)
			);
			const orderedLocations = lodash.sortBy(
				locationsResponse.locations,
				(loc) => loc.name.toLowerCase()
			);
			return orderedLocations;
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export const blueprintManagementPageSlice = createSlice({
	name: 'blueprintManagementPage',
	initialState: initialPageState,
	reducers: {
		addBlueprint(state, action) {
			state.blueprintIds.push(action.payload);
		},
		removeBlueprint(state, action) {
			const blueprintId = action.payload;
			const index = state.blueprintIds.indexOf(blueprintId);
			if (index > -1) {
				state.blueprintIds.splice(index, 1);
			}
		},
		clearBlueprints(state) {
			state.blueprintIds = initialPageState.blueprintIds;
		},
		openDeleteModal(state, action) {
			state.deleteModal.isOpen = true;
			state.deleteModal.blueprintToDelete = action.payload;
		},
		closeDeleteModal(state) {
			state.deleteModal.isOpen = false;
			state.deleteModal.blueprintToDelete = undefined;
		},
		updateSearchTerm(state, action) {
			state.searchTerm = action.payload;
		},
		updateSupplierFilterId(state, action) {
			state.supplierFilterId = action.payload;
		},
		updateLocationFilterIds(state, action) {
			state.locationFilterIds = action.payload;
		},
		updatePageNumber(state, action) {
			state.currentPage = action.payload;
		},
		switchCustomer(state, action) {
			if (state.customerId !== action.payload) {
				state.customerId = action.payload;
				state.currentPage = initialPageState.currentPage;
				state.searchTerm = initialPageState.searchTerm;
				state.supplierFilterId = initialPageState.supplierFilterId;
			}
		},
	},
	extraReducers: (builder) => {
		builder.addCase(getBlueprints.pending, (state) => {
			state.blueprintInProcess = true;
			state.blueprintError = false;
		});
		builder.addCase(getBlueprints.rejected, (state) => {
			state.blueprintInProcess = false;
			state.blueprintError = true;
		});
		builder.addCase(getBlueprints.fulfilled, (state, action) => {
			state.blueprintInProcess = false;
			state.blueprintError = false;
			if (action.payload) {
				state.blueprintIds = action.payload.results.map((x) => x.id);
				state.totalResults = action.payload.totalResults;
				state.totalPages = action.payload.totalPages;
			} else {
				state.blueprintIds = initialPageState.blueprintIds;
				state.totalResults = initialPageState.totalResults;
				state.totalPages = initialPageState.totalPages;
			}
		});
		builder.addCase(deleteBlueprint.pending, (state) => {
			state.deleteModal.inProcess = true;
		});
		builder.addCase(deleteBlueprint.rejected, (state) => {
			state.deleteModal.inProcess = false;
		});
		builder.addCase(deleteBlueprint.fulfilled, (state) => {
			state.deleteModal.inProcess = false;
			state.deleteModal.isOpen = false;
		});
		builder.addCase(getCustomerNote.fulfilled, (state, action) => {
			state.customerNote = action.payload;
		});
		builder.addCase(getCustomerNote.rejected, (state) => {
			state.customerNote = '';
		});
		builder.addCase(fetchLocations.fulfilled, (state, action) => {
			state.locations = action.payload;
		});
	},
});
